'use client'

import { memo } from 'react'
import { IconTag } from '@tabler/icons-react'
import { useRouter } from 'next/navigation'

import {
  AppliedDiscountCode,
  checkValidCode,
  DiscountCode,
  DiscountCodeCard} from 'modules/benefitCodes'
import Button from 'shared/ui/Button'
import { Drawer, DrawerTitle } from 'shared/ui/Drawer'
import Input from 'shared/ui/form/Input'
import LoadingOverlay from 'shared/ui/LoadingOverlay'
import { isNotNilOrEmpty } from 'shared/utils/ramda'
import { addTestId } from 'shared/utils/tests'

import { Cart } from '../../../types'

import CartActiveDiscountCode from './CartActiveDiscountCode'

import styles from './CartBenefitCodesDrawer.module.scss'

const CartBenefitCodesDrawer = memo(
  ({
    open,
    onClose,
    promoCode,
    setPromoCode,
    resetTrigger,
    hasCode,
    handleAddNewCode,
    activeCodes,
    handleRemoveCode,
    providedRebateCodes,
    cart,
    isLoading
  }: {
    open: boolean
    onClose: () => void
    promoCode: string
    setPromoCode: (value: string) => void
    resetTrigger: number
    hasCode: boolean
    handleAddNewCode: (code: string) => void
    activeCodes: AppliedDiscountCode[]
    handleRemoveCode: (codeId: string) => () => void
    providedRebateCodes: DiscountCode[] | null
    cart: Cart | null
    isLoading?: boolean
  }) => {
    const router = useRouter()

    const handleConsentClick = () => {
      onClose()
      router.push('/profil/dane#profile_marketing_consent')
    }

    const handleContinueShopping = () => {
      onClose()
      router.push('/kategorie')
    }

    return (
      <Drawer
        id='cart-rebate-codes-drawer'
        placement='right'
        open={open}
        onClose={onClose}
      >
        <DrawerTitle>Kody Rabatowe</DrawerTitle>
        {isLoading && <LoadingOverlay position='absolute' />}
        <div className={styles.formWrapper}>
          <Input
            placeholder={hasCode ? 'Kod już aktywny' : 'Twój kod rabatowy'}
            size='medium'
            resetValidationTrigger={resetTrigger}
            noMargin
            icons={{
              end: <IconTag aria-hidden='true' />
            }}
            name='promoCode'
            value={promoCode}
            disabled={hasCode}
            onChange={(_, value) => setPromoCode(value)}
          />
          <div className={styles.buttonWrapper}>
            <Button
              fullWidth
              size='medium'
              variant='primary'
              onClick={() => handleAddNewCode(promoCode)}
              disabled={hasCode || promoCode.length < 1}
            >
              Aktywuj
            </Button>
          </div>
        </div>

        {isNotNilOrEmpty(activeCodes) && (
          <>
            <div
              className={styles.activeCodesDescription}
              {...addTestId('active-codes-description-drawer')}
            >
              Aktywowane kody
            </div>
            <ul className={styles.activeCodesList}>
              {activeCodes.map((code: AppliedDiscountCode) => (
                <li className={styles.activeCodesListItem} key={code.id}>
                  <CartActiveDiscountCode
                    code={code}
                    handleRemoveCode={handleRemoveCode(code.id)}
                  />
                </li>
              ))}
            </ul>
          </>
        )}

        {providedRebateCodes && (
          <ul className={styles.rebateCodesWrapper}>
            {providedRebateCodes.map(rebate => {
              const {
                isCodeAvailable,
                isCodeUsed,
                marketingNotConsent,
                minimumOrderValueNotAchieved
              } = checkValidCode(rebate, cart)

              return (
                <li className={styles.rebateCodeListItem} key={rebate.id}>
                  <DiscountCodeCard
                    rebate={rebate}
                    isCodeAvailable={isCodeAvailable}
                    isCodeUsed={isCodeUsed}
                    marketingNotConsent={marketingNotConsent}
                    minimumOrderValueNotAchieved={minimumOrderValueNotAchieved}
                    activeCodeIDs={activeCodes.map(code => code.id)}
                    onClick={() => {
                      if (isCodeAvailable) return handleAddNewCode(rebate.code)
                      if (marketingNotConsent) return handleConsentClick()
                      if (
                        rebate.minimumOrderValue &&
                        minimumOrderValueNotAchieved
                      )
                        return handleContinueShopping()
                    }}
                  />
                </li>
              )
            })}
          </ul>
        )}
      </Drawer>
    )
  }
)

export default CartBenefitCodesDrawer
